<template>
  <div class="meeting-container" :class="{ 'is-flex': isPostCover }">
    <div class="columns is-centered" :class="{ 'post-cover': isPostCover }">
      <div class="column is-narrow">
        <div class="card">
          <div class="card-content">
            <div class="time columns">
              <div class="column">
                <div class="columns is-mobile">
                  <div class="column hour is-narrow">{{meetingHour}}</div>
                  <div class="column minute is-narrow pr-2">
                    <div>{{meetingMinutes}}</div>
                    <div class="ampm">{{meetingAMPM}}</div>
                  </div>
                </div>
              </div>
              <div class="column calendar">
                <div class="weekday">{{meetingWeekDay}}</div>
                <div class="date">{{meetingDay}} . {{meetingMonth}} {{meetingYear}}</div>
                <div class="timezone">{{meetingTimezone}}</div>
              </div>
            </div>
            <div class="content meetingDetails">
              <div><span class="container"><i class="fal fa-pencil-alt"></i></span>{{title}}</div>
              <div><a class="is-black" :href="meetingUrl" target="_blank"><span class="container"><i class="fal fa-link"></i></span>teams.microsoft.com</a></div>
              <div><span class="container"><i class="fal fa-user"></i></span>{{student}}</div>
              <div v-if="isJudgeTeamProject"><span class="container"><i class="fal fa-users"></i></span>{{judges}}</div>
              <div class="project-title" v-if="meetingProjectTitle">
                <router-link :to="`/project/${meetingProjectID}`"
                             target="_blank"
                             :event="'click'"><span class="container"><i class="fal fa-file"></i></span>{{meetingProjectTitle}}</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import imageProxy from '@/libs/image-proxy'
import componentState from '@/mixins/component-state'
import displayTypes from '@/mixins/project-display-types'

export default {
  props: {
    attachment: {
      type: Object
    },
    isPostCover: {
      type: Boolean,
      default: false
    }
  },
  mixins: [componentState, displayTypes],
  data: function () {
    return {
      error: { favicon: false, image: false }
    }
  },
  computed: {
    project: function () {
      const post = this.$store.getters.getPost(this.attachment.postID)
      return this.$store.getters.project(post.projectID)
    },
    isJudgeTeamProject: function () {
      return this.project.displayType === this.DisplayTypeJudgeTeam
    },
    metadata: function () {
      return this.attachment.metadata || {}
    },
    meeting: function () {
      return this.attachment.meeting || {}
    },
    meetingDateTime: function () {
      if (this.meeting) {
        return new Date(this.meeting.start)
      }
      return null
    },
    meetingHour: function () {
      return new Intl.DateTimeFormat('en', { hour: '2-digit' }).format(this.meetingDateTime).replace('AM', '').replace('PM', '').trim()
    },
    meetingMinutes: function () {
      // https://stackoverflow.com/a/60584726
      // A bug in V8 causes this to not work as expected. Until this is fixed, let's pad it manually
      return new Intl.DateTimeFormat('en', { minute: '2-digit' }).format(this.meetingDateTime).padStart(2, '0')
    },
    meetingAMPM: function () {
      return new Intl.DateTimeFormat('en', { hour: '2-digit' }).format(this.meetingDateTime).split(' ')[1]
    },
    meetingWeekDay: function () {
      return new Intl.DateTimeFormat('en', { weekday: 'long' }).format(this.meetingDateTime)
    },
    meetingDay: function () {
      return new Intl.DateTimeFormat('en', { day: 'numeric' }).format(this.meetingDateTime)
    },
    meetingMonth: function () {
      return new Intl.DateTimeFormat('en', { month: 'short' }).format(this.meetingDateTime)
    },
    meetingYear: function () {
      return new Intl.DateTimeFormat('en', { year: 'numeric' }).format(this.meetingDateTime)
    },
    meetingTimezone: function () {
      return new Intl.DateTimeFormat('en', { timeZoneName: 'short' }).format(this.meetingDateTime).split(',')[1].trim()
    },
    meetingUrl: function () {
      return this.meeting.meetingUrl
    },
    image: function () {
      return this.imageSrc ? imageProxy.proxy(this.imageSrc, { width: 515, height: 386 }) : '' // image is 4x3
    },
    imageSrc: function () {
      if (this.error.image) {
        return ''
      }

      if (this.metadata.ogImage) {
        return this.metadata.ogImage
      }

      if (this.metadata.screenshot) {
        return this.metadata.screenshot
      }

      return ''
    },
    description: function () {
      if (this.metadata.ogDescr) {
        return this.metadata.ogDescr
      }

      return this.metadata.descr
    },
    title: function () {
      if (this.metadata.ogTitle) {
        return this.metadata.ogTitle
      }

      return this.metadata.title
    },
    site: function () {
      if (this.metadata.ogName) {
        return this.metadata.ogName
      }

      const parts = this.metadata.url.match(/\/\/([^/]+)/)
      if (parts.length > 1) {
        return parts[1]
      }

      return this.metadata.url
    },
    favicon: function () {
      return this.faviconSrc ? imageProxy.proxy(this.faviconSrc, {}) : ''
    },
    faviconSrc: function () {
      return !this.error.favicon ? this.metadata.favicon : ''
    },
    student: function () {
      if (this.metadata.student) {
        return this.metadata.student
      }
      return ''
    },
    judges: function () {
      if (this.metadata.judges) {
        return this.metadata.judges
      }
      return ''
    },
    meetingProjectTitle: function () {
      if (this.metadata.projectTitle) {
        return this.metadata.projectTitle
      }
      return ''
    },
    meetingProjectID: function () {
      if (this.metadata.projectID) {
        return this.metadata.projectID
      }
      return ''
    }
  },
  methods: {
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.meeting-container {
  font-size: 16px;
  background: linear-gradient(30deg, #531c80, #861ce6);
  font-family: Poppins;
  .post-cover {
    font-size: 6px;
    margin: 0 auto;
    align-self: flex-end;
    padding-bottom: 1.5em;
    >.column {
      padding: 0;
    }
  }
}

@media only screen and (max-width: 768px) {
  .post-cover {
    padding: 0;
    .meetingDetails {
      display: none;
    }
  }
}

.meetingDetails .project-title {
  max-width: 25em;
}

.calendar {
  align-self: center;
  border-left: 0.2em solid black;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  padding-left: 0.5em;
}

.hour {
  font-size: 8.5em;
  line-height: 1em;
  padding: 0;
  margin: 0;
  font-weight: bold;
}
.minute {
  padding: 0;
  margin: 0;
  flex-direction: column;
  align-self: center;
  font-weight: bold;
  div {
    line-height: 1em;
    font-size: 3.5em;
    padding: 0;
    margin: 0;
  }
  .ampm {
    font-weight: normal;
  }
}
.weekday, .date, .timezone {
  font-size: 2em;
  line-height: 1em;
  padding: 0;
  margin: 0;
  text-transform: uppercase;
  align-content: center;
}
.date {
  white-space: nowrap;
  font-size: 1.2em;
  padding-top: 0.4em;
}
.timezone {
  font-size: 1.2em;
}

.card {
  background-color: #a042ee;
  color: white;
  padding-bottom: 1em;
  border-radius: 10px;
  margin: 1em;
}

.time.columns {
  margin:0;
}

.card-content {
  padding: 1.3em;
}

.container {
  background-color: #8929de;
  margin-right: 0.7em;
  padding: 0.3em 0.3em 0.3em 0.3em;
  border-radius: 5px;
}

.card .content div {
  padding-top: 0.5em;
  padding-bottom: 0.5em;
}

  .card a {
    color: white;
  }

  .title {
    font-size: 1.1em;
    font-weight: 700;
    line-height: 1.5em;
  }

</style>
